import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Timer, MessageSquare, X, Clock, ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react'
import { chatAPI } from '@renderer/api/chat'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'

interface SessionSummary {
  id: string
  title: string
  exercise_id: number
  exercise_title?: string
  course_name?: string
  created_at: string
  updated_at: string
  messages: Array<{ id: string; content: string; sender: string; created_at: string }>
}

interface TranscriptMessage {
  id: string
  content: string
  sender: string
  created_at: string
}

const ITEMS_PER_PAGE = 10

function stripMeta(content: string) {
  return content
    .replace(/\n?<!-- meta \{.*?\} -->/g, '')
    .replace(/\[(?:INSTRUCTOR CONTEXT|ADVANCE CONFIRMED)[^\[]*?\]/gs, '')
    .trim()
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function duration(created: string, updated: string) {
  const ms = new Date(updated).getTime() - new Date(created).getTime()
  if (ms < 60_000) return '<1m'
  const m = Math.floor(ms / 60_000)
  const h = Math.floor(m / 60)
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`
}

export const LabSessionsPage = () => {
  const { setBreadcrumbs } = useBreadcrumbs()
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [viewing, setViewing] = useState<SessionSummary | null>(null)
  const [transcriptMessages, setTranscriptMessages] = useState<TranscriptMessage[]>([])
  const [loadingTranscript, setLoadingTranscript] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setBreadcrumbs([{ label: 'Sessions' }])
  }, [])

  useEffect(() => {
    chatAPI.getConversations()
      .then((data) => setSessions(data))
      .catch((e) => console.error('Failed to fetch sessions:', e))
      .finally(() => setLoading(false))
  }, [])

  const handleDownloadById = async (id: string) => {
    try {
      const res = await chatAPI.downloadTranscript(id)
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transcript-${id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Failed to download transcript:', e)
    }
  }

  const handleDownload = async () => {
    if (!viewing) return
    handleDownloadById(viewing.id)
  }

  const handleViewTranscript = async (s: SessionSummary) => {
    setViewing(s)
    setTranscriptMessages([])
    setLoadingTranscript(true)
    try {
      const data = await chatAPI.getConversation(s.id)
      setTranscriptMessages(data.messages || [])
    } catch (e) {
      console.error('Failed to load transcript:', e)
    } finally {
      setLoadingTranscript(false)
    }
  }

  useEffect(() => {
    if (viewing) scrollRef.current?.scrollTo({ top: 0 })
  }, [transcriptMessages, viewing])

  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE)
  const paginated = sessions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#b8923a' }} />
          <p className="text-sm" style={{ color: '#7a6e54' }}>Loading sessions…</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: 'Sora, system-ui, sans-serif' }}>
      <div className="mx-auto w-full max-w-5xl space-y-6">

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            className="rounded-2xl p-5 flex flex-col gap-3 border"
            style={{ background: '#faf4e3', borderColor: '#e4d6b0' }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(184,146,58,0.15)' }}
            >
              <Timer className="w-4 h-4" style={{ color: '#b8923a' }} />
            </div>
            <div>
              <span className="text-3xl font-bold" style={{ color: '#1d160a' }}>{sessions.length}</span>
              <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#b8923a' }}>
                Total Sessions
              </p>
            </div>
          </div>

          <div
            className="rounded-2xl p-5 flex flex-col gap-3 border"
            style={{ background: '#faf4e3', borderColor: '#e4d6b0' }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(184,146,58,0.15)' }}
            >
              <Clock className="w-4 h-4" style={{ color: '#b8923a' }} />
            </div>
            <div>
              <span className="text-3xl font-bold" style={{ color: '#1d160a' }}>
                {new Set(sessions.map((s) => s.exercise_id)).size}
              </span>
              <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#b8923a' }}>
                Exercises Practiced
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ background: '#ffffff', borderColor: '#e4d6b0', boxShadow: '0 2px 12px rgba(74,63,42,0.06)' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead
                className="border-b text-left"
                style={{ background: '#faf4e3', borderColor: '#e4d6b0' }}
              >
                <tr>
                  {['Course', 'Exercise', 'Start Time', 'End Time', 'Duration', 'Transcript'].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.08em]"
                      style={{ color: '#b8923a' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ borderColor: '#f5ede0' }}>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-14 text-center">
                      <MessageSquare className="mx-auto w-10 h-10 mb-3" style={{ color: '#e4d6b0' }} />
                      <p className="text-sm" style={{ color: '#a89c7e' }}>No sessions found</p>
                    </td>
                  </tr>
                ) : (
                  paginated.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t transition-colors"
                      style={{ borderColor: '#f5ede0' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#fdfaf4')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td className="px-4 py-3.5 max-w-[160px]">
                        <span
                          className="text-sm truncate block max-w-full"
                          style={{ color: '#4a3f2a' }}
                          title={s.course_name || undefined}
                        >
                          {s.course_name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 max-w-[200px]">
                        <span
                          className="inline-block px-2.5 py-1 rounded-lg text-xs font-medium truncate max-w-full"
                          style={{ background: '#faf4e3', color: '#7a5a1e' }}
                          title={s.exercise_title || undefined}
                        >
                          {s.exercise_title || 'Unknown exercise'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm" style={{ color: '#4a3f2a' }}>
                        {formatDate(s.created_at)}
                      </td>
                      <td className="px-4 py-3.5 text-sm" style={{ color: '#7a6e54' }}>
                        {formatDate(s.updated_at)}
                      </td>
                      <td className="px-4 py-3.5 text-sm" style={{ color: '#7a6e54' }}>
                        {duration(s.created_at, s.updated_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="relative group/eye">
                            <button
                              onClick={() => handleViewTranscript(s)}
                              className="grid place-items-center w-8 h-8 rounded-lg border transition-colors"
                              style={{ background: '#faf4e3', borderColor: '#e4d6b0', color: '#7a5a1e' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f0e4c4')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '#faf4e3')}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-medium opacity-0 group-hover/eye:opacity-100 transition-opacity"
                              style={{ background: '#1d160a', color: '#fff' }}>
                              View transcript
                            </span>
                          </div>
                          <div className="relative group/dl">
                            <button
                              onClick={() => handleDownloadById(s.id)}
                              className="grid place-items-center w-8 h-8 rounded-lg border transition-colors"
                              style={{ background: '#faf4e3', borderColor: '#e4d6b0', color: '#7a5a1e' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f0e4c4')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '#faf4e3')}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-medium opacity-0 group-hover/dl:opacity-100 transition-opacity"
                              style={{ background: '#1d160a', color: '#fff' }}>
                              Download PDF
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sessions.length > ITEMS_PER_PAGE && (
            <div
              className="flex items-center justify-between px-4 py-3 border-t text-sm"
              style={{ borderColor: '#e4d6b0', color: '#7a6e54' }}
            >
              <span>
                {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, sessions.length)} of {sessions.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="w-7 h-7 grid place-items-center rounded-lg border transition-colors disabled:opacity-40"
                  style={{ borderColor: '#e4d6b0' }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#faf4e3')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="w-7 h-7 grid place-items-center rounded-lg border transition-colors disabled:opacity-40"
                  style={{ borderColor: '#e4d6b0' }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#faf4e3')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {viewing && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setViewing(null)}
        >
          <div
            className="relative flex flex-col rounded-2xl shadow-2xl overflow-hidden w-full max-w-2xl"
            style={{ maxHeight: '80vh', background: '#ffffff', border: '1px solid #e4d6b0' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="shrink-0 flex items-center justify-between px-6 py-4 border-b"
              style={{ background: '#faf4e3', borderColor: '#e4d6b0' }}
            >
              <div>
                <h2 className="text-sm font-semibold" style={{ color: '#1d160a' }}>
                  {viewing.title || 'Session Transcript'}
                </h2>
                <p className="text-[11px] mt-0.5" style={{ color: '#a89c7e' }}>
                  {formatDate(viewing.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleDownload}
                  disabled={loadingTranscript || transcriptMessages.length === 0}
                  title="Download transcript"
                  className="grid place-items-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40"
                  style={{ color: '#7a6e54' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f0e4c4')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewing(null)}
                  className="grid place-items-center w-7 h-7 rounded-lg transition-colors"
                  style={{ color: '#7a6e54' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f0e4c4')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {loadingTranscript ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#b8923a' }} />
                </div>
              ) : transcriptMessages.length === 0 ? (
                <p className="text-center text-sm py-10" style={{ color: '#a89c7e' }}>
                  No messages in this session.
                </p>
              ) : (
                transcriptMessages.map((msg) => {
                  const isUser = msg.sender === 'user'
                  const text = stripMeta(msg.content)
                  if (!text) return null
                  return (
                    <div key={msg.id} className="flex gap-3">
                      <div className="w-12 shrink-0 pt-0.5 text-right">
                        <span
                          className="text-[11px] font-bold uppercase tracking-wide"
                          style={{ color: isUser ? '#4a3f2a' : '#b8923a' }}
                        >
                          {isUser ? 'You' : 'Agent'}
                        </span>
                      </div>
                      <div className="mt-1.5 w-px shrink-0 self-stretch" style={{ background: '#e4d6b0' }} />
                      <div className="flex-1 pb-2">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px]" style={{ color: '#c8b88a' }}>
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words" style={{ color: '#33383d' }}>
                          {text}
                        </p>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  )
}
